'use client';

import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import styles from './Header.module.css';
import SearchResultsCard from './SearchResultsCard/SearchResultsCard';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { clientLog } from "../../../utils/log";

const Header = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [allMangas, setAllMangas] = useState([]);
  const [showDevNotice, setShowDevNotice] = useState(false);
  const [noticePosition, setNoticePosition] = useState({ x: 0, y: 0 });
  const [noticeTarget, setNoticeTarget] = useState(null);
  const [isLoadingAllMangas, setIsLoadingAllMangas] = useState(false);
  const searchRef = useRef(null);
  const noticeTimerRef = useRef(null);
  const router = useRouter();

  // Функция для показа уведомления "В разработке"
  const showDevelopmentNotice = (e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    setNoticePosition({
      x: rect.left + rect.width / 2,
      y: rect.bottom + 5
    });
    setShowDevNotice(true);
    
    if (noticeTimerRef.current) {
      clearTimeout(noticeTimerRef.current);
    }
    
    noticeTimerRef.current = setTimeout(() => {
      setShowDevNotice(false);
    }, 2000);
  };

  const showDevelopmentNoticeButton = (e) => {
      const rect = e.currentTarget.getBoundingClientRect();
      setNoticePosition({
          x: rect.left + rect.width / 2,
          y: rect.bottom + 5 // Позиционируем сверху
      });
      setNoticeTarget(e.currentTarget);
      setShowDevNotice(true);
      
      if (noticeTimerRef.current) {
          clearTimeout(noticeTimerRef.current);
      }
      
      noticeTimerRef.current = setTimeout(() => {
          setShowDevNotice(false);
      }, 2000);
  };

  // Функция для исправления раскладки клавиатуры
  const fixKeyboardLayout = (str) => {
    const en = "qwertyuiop[]asdfghjkl;'zxcvbnm,./`QWERTYUIOP{}ASDFGHJKL:\"ZXCVBNM<>?~@#$^&";
    const ru = "йцукенгшщзхъфывапролджэячсмитьбю.ёЙЦУКЕНГШЩЗХЪФЫВАПРОЛДЖЭЯЧСМИТЬБЮ,Ё\"№;:?";
    
    let fixedStr = "";
    
    for (let char of str) {
      const enIndex = en.indexOf(char);
      const ruIndex = ru.indexOf(char);
      
      if (enIndex !== -1) {
        fixedStr += ru[enIndex];
      } else if (ruIndex !== -1) {
        fixedStr += en[ruIndex];
      } else {
        fixedStr += char;
      }
    }
    
    return fixedStr;
  };

  // Функция для экранирования специальных символов в регулярных выражениях
  const escapeRegExp = (string) => {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  };

  // Нормализация букв е/ё
  const normalizeE = (str) => {
    return escapeRegExp(str).replace(/[её]/gi, '(е|ё)');
  };

  // Загрузка всех манг с пагинацией
  useEffect(() => {
    const loadAllMangas = async () => {
      try {
        setIsLoadingAllMangas(true);
        let allMangasData = [];
        let offset = 0;
        const limit = 20;
        let hasMore = true;

        // Загружаем манги пачками по 100 пока не получим все
        while (hasMore) {
          const mangaData = {
            "limit": limit,
            "offset": offset,
            "sort_by": "avg_rating",
            "sort_order": "desc"
          };

          const response = await axios.post(
            `${process.env.NEXT_PUBLIC_API_URL || ""}/api/v2/projects/filter`, 

            mangaData,
            { headers: { 'Content-Type': 'application/json' } }
          );

          if (response.data.length === 0) {
            hasMore = false;
            break;
          }

          const parsedMangas = response.data.map(manga => ({
            id: manga.id,
            name: manga.title,
            type: manga.type,
            cover_url:  
              manga.cover_image?.core_url ||
              manga.cover_image?.selectel_url ||
              manga.cover_url,
            transliterated_name: manga.transliterated_name
          }));

          allMangasData = [...allMangasData, ...parsedMangas];
          offset += limit;

          // Если получили меньше чем limit, значит это последняя страница
          if (response.data.length < limit) {
            hasMore = false;
          }

          // Небольшая задержка между запросами чтобы не нагружать сервер
          await new Promise(resolve => setTimeout(resolve, 100));
        }

        setAllMangas(allMangasData);
      } catch (err) {
        clientLog("error", `[Header] Ошибка при загрузке манг: ${err.message}`);
      } finally {
        setIsLoadingAllMangas(false);
      }
    };

    loadAllMangas();
  }, []);

  // Поиск манги
  const searchMangas = (query) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    const correctedQuery = fixKeyboardLayout(query);

    try {
      const normalizedQuery = normalizeE(query.toLowerCase());
      const normalizedCorrected = normalizeE(correctedQuery.toLowerCase());

      const regexQuery = new RegExp(normalizedQuery, 'i');
      const regexCorrected = new RegExp(normalizedCorrected, 'i');

      const filtered = allMangas.filter(
        (manga) =>
          regexQuery.test(manga.name.toLowerCase()) ||
          regexCorrected.test(manga.name.toLowerCase())
      );

      // ✅ уникализация
      const uniqueResults = Array.from(
        new Map(filtered.map((m) => [m.id, m])).values()
      );

      setSearchResults(uniqueResults.slice(0, 5));
    } catch (error) {
      clientLog("error", `[Header] Ошибка при создании регулярного выражения: ${error.message}`);
      const queryLower = query.toLowerCase();
      const correctedLower = correctedQuery.toLowerCase();

      const filtered = allMangas.filter((manga) => {
        const mangaNameLower = manga.name.toLowerCase();
        return (
          mangaNameLower.includes(queryLower) ||
          mangaNameLower.includes(correctedLower)
        );
      });

      const uniqueResults = Array.from(
        new Map(filtered.map((m) => [m.id, m])).values()
      );

      setSearchResults(uniqueResults.slice(0, 5));
    }
  };

  // ⏳ дебаунс поиска
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchQuery.trim()) {
        setIsSearching(true);
        searchMangas(searchQuery);
        setIsSearching(false);
      } else {
        setSearchResults([]);
      }
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery, allMangas]);

  // 🧹 очистка таймера
  useEffect(() => {
    return () => {
      if (noticeTimerRef.current) {
        clearTimeout(noticeTimerRef.current);
      }
    };
  }, []);

  const openTelegram = () => {
    window.open('https://t.me/+igthUWdHxoJlNDhi', '_blank');
  };

  const handleLogoClick = () => {
    router.push(`/`);
  };

  const openCatalog = () => {
    router.push(`/catalog`);
  };

  // Закрытие списка при клике вне области
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setSearchQuery('');
        setSearchResults([]);
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Переход к манге
  const handleSuggestionClick = (mangaId) => {
    router.push(`/manga/${mangaId}`);
    setSearchQuery('');
    setShowSuggestions(false);
  };

  // Поиск при нажатии Enter
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      router.push(`/search?query=${encodeURIComponent(searchQuery)}`);
      setSearchQuery('');
      setShowSuggestions(false);
    }
  };

  const handleClearSearch = () => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSuggestions(false);
  };

  return (
    <header className={styles.header}>
      <nav className={styles.nav}>
        <div className={styles.nav_logo} onClick={handleLogoClick}>
          <img className={styles.nav_logo} src="/logo/ManGO_crashed.png" alt="Logo"/>
        </div>
        <div className={styles.searchContainer} ref={searchRef}>
          <div className={styles.searchForm}>
            <img alt="" src="/icons/find_vector.svg" style={{ flex: 1 }} />

            <input
              type="text"
              placeholder="Поиск"
              className={styles.searchInput}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => setShowSuggestions(true)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault(); // полностью блокируем стандартное действие Enter
                }
              }}
            />

            {searchQuery && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="25px"
                height="39px"
                viewBox="0 0 24 24"
                onClick={handleClearSearch}
              >
                <path
                  fill="#D9D9D9"
                  stroke="#D9D9D9"
                  strokeLinecap="round"
                  strokeWidth="2"
                  d="M20 20L4 4m16 0L4 20"
                />
              </svg>
            )}
          </div>
          {showSuggestions && (
            <div
              className={styles.suggestionsContainer}
              aria-busy={isLoadingAllMangas ? 'true' : 'false'}
            >
              {/* если идёт загрузка всех манг — показываем лоадер внутри выпадающего списка */}
              {isLoadingAllMangas ? (
                <div className={styles.statusMessage}>
                  <div className={styles.statusContent}>
                    <img
                      src="/logo/logo_mini_256_256_2.png"
                      alt="Загрузка..." 
                      className={styles.loading_icon}
                    />
                    <p style={{transform: "translateY(-30px)", marginBottom: "0px"}}>Загрузка...</p>
                  </div>
                </div>
              ) : isSearching ? (
                <div className={styles.suggestionItem}>Загрузка...</div>
              ) : searchResults.length > 0 && searchQuery.trim() ? (
                searchResults.map((result) => (
                  <div
                    key={result.id}
                    className={styles.suggestionItem}
                    onClick={() => handleSuggestionClick(result.transliterated_name)}
                  >
                    <SearchResultsCard manga={result} />
                  </div>
                ))
              ) : searchQuery.trim() ? (
                <div className={styles.suggestionItem}>
                  <div className={styles.nothing_found_cont}>
                    <img className={styles.nothing_found} alt="" src="/icons/not_found.svg"/>
                    <p className={styles.nothing_found_text}>Ничего не найдено</p>
                  </div>
                </div>
              ) : <p>Введите что либо...</p>}
            </div>
          )}
        </div>
        <Link
          href=""
          onClick={(e) => {
            e.preventDefault(); // отменяем переход
            showDevelopmentNoticeButton(e); // передаём событие в твой хэндлер
          }}
          className={`${styles.header_icon} ${showSuggestions ? styles.find_smth : ''}`}
        >
          <img className={styles.header_icon_img} src="/logo/logo_mini_256_256_2.png" alt="Профиль"/>        
        </Link>
      </nav>
      <div className={styles.controlWrapper}>
        <div className={styles.panelElem} onClick={openTelegram}>
            <p className={styles.buttonLabel}>
              <svg className={styles.buttonIcon} xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 600 600">
                <defs>
                  <linearGradient id="gradient1" x1="0%" y1="50%" x2="100%" y2="50%">
                    <stop offset="0%" stopColor="#EEBE28" stopOpacity="1"/>
                    <stop offset="100%" stopColor="#EEBE28" stopOpacity="1"/>
                  </linearGradient>
                </defs>
                <path d="M20.4692 312.929L54.161 324.11C67.569 329.531 122.232 342.745 143.548 344.778C160.6 346.404 186.407 333.258 197.18 326.482L424.428 177.062C428.095 174.69 436.735 171.166 441.961 176.045C447.187 180.924 444.138 186.888 441.961 189.259L293.786 329.87C281.295 340.825 256.175 365.92 255.625 378.66C255.074 391.400 268.918 404.072 275.908 408.815C323.237 440.213 421.746 505.582 437.148 515.882C456.401 528.758 470.496 537.228 493.53 532.823C511.958 529.3 522.524 497.021 525.503 481.323C544.756 373.578 583.879 148.194 586.355 108.62C589.449 59.1524 581.542 55.7642 559.883 54.0701C542.555 52.7148 492.384 71.3499 469.465 80.8369C333.093 136.629 51.892 253.025 18.0626 272.27C-15.7667 291.516 5.57143 307.395 20.4692 312.929Z" fill="url(#gradient1)"/>
              </svg>
              <span>Мы в тг</span></p>
        </div>
        <div className={styles.panelElem} onClick={openCatalog}>
            <p className={styles.buttonLabel}>
              <svg style={{transform: "translateX(-5px) scale(0.85)"}} xmlns="http://www.w3.org/2000/svg" width="37px" height="25px" viewBox="1150 0 450 600">
                <defs>
                  <linearGradient id="solidGold" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#EEBE28"/>
                    <stop offset="100%" stopColor="#EEBE28"/>
                  </linearGradient>
                </defs>
                <rect x="1171" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1384" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1597" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1171" y="213" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1384" y="213" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1597" y="213" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1171" y="426" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1384" y="426" width="162" height="162" rx="39" fill="url(#solidGold)"/>
                <rect x="1597" y="426" width="162" height="162" rx="39" fill="url(#solidGold)"/>
              </svg>
              <span>Каталог</span></p>
        </div>
        <div className={styles.panelElem} onClick={showDevelopmentNotice}>
            <p className={styles.buttonLabel}>
              <svg className={styles.buttonIcon} style={{transform: "translateX(-0px) scale(0.86)"}} xmlns="http://www.w3.org/2000/svg" width="36px" height="25px" viewBox="5300 0 600 600">
                <defs>
                  <linearGradient id="gradient3" x1="0%" y1="50%" x2="100%" y2="50%">
                    <stop offset="0%" stopColor="#F5B740"/>
                    <stop offset="100%" stopColor="#FFAA77"/>
                  </linearGradient>
                </defs>
                <path d="M5392.91 581.145C5404.1 586.894 5416.48 588.455 5428.42 585.625C5440.36 582.795 5451.32 575.705 5459.86 565.281L5552.89 454.093C5567.28 436.895 5593.72 436.895 5608.11 454.093L5701.14 565.281C5706.77 572.139 5713.47 577.589 5720.86 581.314C5728.25 585.039 5736.18 586.966 5744.19 586.984C5752.43 586.955 5760.58 584.971 5768.17 581.145C5779.46 575.657 5789.11 566.207 5795.85 554.029C5802.6 541.851 5806.13 527.512 5806 512.883V124.173C5805.96 91.5176 5795.15 60.2107 5775.94 37.1198C5756.72 14.0288 5730.67 1.03912 5703.5 1L5457.5 1C5430.33 1.03912 5404.28 14.0288 5385.06 37.1198C5365.85 60.2107 5355.04 91.5176 5355 124.173V512.883C5354.87 527.523 5358.42 541.871 5365.18 554.05C5371.94 566.229 5381.61 575.672 5392.91 581.145Z" fill="url(#gradient3)"/>
              </svg>
              <span>Закладки</span></p>
        </div>
        <div className={styles.panelElem} onClick={showDevelopmentNotice}>
            <p className={styles.buttonLabel}>
              <svg className={styles.buttonIcon} style={{transform: "translateY(0px) scale(1.00)"}} xmlns="http://www.w3.org/2000/svg" width="29px" height="25px" viewBox="6300 0 600 600">
                <defs>
                  <linearGradient id="gradient4" x1="0%" y1="50%" x2="100%" y2="50%">
                    <stop offset="0%" stopColor="#FEAA75"/>
                    <stop offset="100%" stopColor="#FFA884"/>
                  </linearGradient>
                </defs>
                <path d="M6324.25 142.451C6318.56 121.058 6332.9 99.4239 6354.48 94.5363C6359.45 93.4145 6477.85 67 6617.05 67C6756.26 67 6874.68 93.4145 6879.65 94.5363C6901.23 99.4239 6914.75 120.871 6909.86 142.451C6904.97 164.031 6883.47 177.546 6861.94 172.685C6860.82 172.444 6747.26 147.125 6617.05 147.125C6486.85 147.125 6373.29 172.444 6372.17 172.685C6369.2 173.353 6334.08 179.415 6324.25 142.451Z" fill="url(#gradient4)"/>
                <path d="M6324.25 316.126C6318.56 294.732 6332.9 273.098 6354.48 268.211C6359.45 267.089 6477.85 240.675 6617.05 240.675C6756.26 240.675 6874.68 267.089 6879.65 268.211C6901.23 273.098 6914.75 294.545 6909.86 316.126C6904.97 337.706 6883.47 351.22 6861.94 346.359C6860.82 346.119 6747.26 320.8 6617.05 320.8C6486.85 320.8 6373.29 346.119 6372.17 346.359C6369.2 347.027 6334.08 353.09 6324.25 316.126Z" fill="url(#gradient4)"/>
                <path d="M6324.25 489.8C6318.56 468.407 6332.9 446.773 6354.48 441.885C6359.45 440.764 6477.85 414.349 6617.05 414.349C6756.26 414.349 6874.68 440.764 6879.65 441.885C6901.23 446.773 6914.75 468.22 6909.86 489.8C6904.97 511.381 6883.47 524.895 6861.94 520.034C6860.82 519.794 6747.26 494.474 6617.05 494.474C6486.85 494.474 6373.29 519.794 6372.17 520.034C6369.2 520.702 6334.08 526.765 6324.25 489.8Z" fill="url(#gradient4)"/>
              </svg>
              <span>Меню</span></p>
        </div>
      </div>
      {showDevNotice && (
          <div 
            className={styles.devNotice} 
            style={{
              left: `${noticePosition.x}px`,
              top: `${noticePosition.y}px`,
              transform: 'translateX(-50%)'
            }}
          >
            В разработке
          </div>
        )}
    </header>
  );
};

export default Header;